import React, { useState, useMemo } from 'react';
import { getAllLots, Lot } from '../../2-ratio/data/lotsData';
import { allData as ouvragesData, Ouvrage } from '../../1-bibliotheque-prix/data/ServicesData';
import { getAllEnsembles } from '../data/ensemblesData';
import { Ensemble } from '../types/EnsembleTypes';
import { FPLabel } from './atoms';
import { computeBenchmarkStats } from '../../6-challenger-devis/utils/benchmarkStats';
import { BlockCalculationService } from '../services';

interface SearchResult {
  id: string;
  type: 'lot' | 'prestation' | 'ensemble';
  nom: string;
  description?: string;
  categorie: string;
  unite?: string;
  ensembleMode?: 'groupe' | 'ventilation';
  data: Lot | Ouvrage | Ensemble;
}

interface ChiffMenuSearchBarProps {
  onResultSelect: (result: SearchResult) => void;
  className?: string;
  /** Ensembles créés à la volée — fusionnés avec les prédéfinis, affichés en tête. */
  userEnsembles?: Ensemble[];
}

const ChiffMenuSearchBar: React.FC<ChiffMenuSearchBarProps> = ({ onResultSelect, className = '', userEnsembles }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Fonction de recherche unifiée
  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return [];

    const query = searchQuery.toLowerCase();
    const results: SearchResult[] = [];

    // Recherche dans les lots RATIO
    const lots = getAllLots();
    lots.forEach(lot => {
      if (
        lot.nom.toLowerCase().includes(query) ||
        lot.description?.toLowerCase().includes(query) ||
        lot.categorie.toLowerCase().includes(query)
      ) {
        results.push({
          id: `lot-${lot.id}`,
          type: 'lot',
          nom: lot.nom,
          description: lot.description,
          categorie: lot.categorie,
          unite: lot.unite,
          data: lot
        });
      }
    });

    // Recherche dans les prestations (Bibliothèque de prix)
    // On inclut F+P (fourni posé — cas par défaut d'une ligne de chiffrage)
    // ET F (fourniture nue — cas d'un achat matière revendu seul). Le FPLabel
    // dans le dropdown lève l'ambiguïté visuelle. Le handler côté container
    // détecte le type et pré-remplit la décomposition sur un item F (mode
    // 'fourni-pose' si l'ouvrage porte des refs MO, sinon 'fourniture-seule').
    ouvragesData.forEach(ouvrage => {
      if (
        ouvrage.intitule.toLowerCase().includes(query) ||
        ouvrage.categorie.toLowerCase().includes(query)
      ) {
        results.push({
          id: `prestation-${ouvrage.id}`,
          type: 'prestation',
          nom: ouvrage.intitule,
          description: ouvrage.categorie,
          categorie: ouvrage.categorie,
          unite: ouvrage.unite,
          data: ouvrage
        });
      }
    });

    // Recherche dans les ensembles — user d'abord (plus récents), puis prédéfinis.
    // Les deux modes ('groupe' et 'ventilation') sont inclus : le handler côté
    // container (handleAddEnsemble) sait déjà traiter les deux.
    const ensembles = [...(userEnsembles ?? []), ...getAllEnsembles()];
    ensembles.forEach(ensemble => {
      if (
        ensemble.nom.toLowerCase().includes(query) ||
        ensemble.description.toLowerCase().includes(query) ||
        ensemble.categorie.toLowerCase().includes(query) ||
        ensemble.tags.some(tag => tag.toLowerCase().includes(query))
      ) {
        results.push({
          id: `ensemble-${ensemble.id}`,
          type: 'ensemble',
          nom: ensemble.nom,
          description: ensemble.description,
          categorie: ensemble.categorie,
          ensembleMode: ensemble.mode,
          data: ensemble
        });
      }
    });

    return results.slice(0, 10); // Limiter à 10 résultats
  }, [searchQuery, userEnsembles]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleResultSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleResultSelect = (result: SearchResult) => {
    console.log('ChiffMenuSearchBar: handleResultSelect appelé avec:', result);
    onResultSelect(result);
    setSearchQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Délai pour permettre le clic sur un résultat
    setTimeout(() => setIsOpen(false), 300);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="Rechercher des lots, prestations ou ensembles..."
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
      </div>

      {/* Dropdown des résultats */}
      {isOpen && searchResults.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {searchResults.map((result, index) => (
            <div
              key={result.id}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                index === selectedIndex
                  ? 'bg-violet-600 text-white'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
              onClick={() => handleResultSelect(result)}
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {result.type === 'prestation' && (
                      <FPLabel ouvrageType={(result.data as Ouvrage).type} />
                    )}
                    <span className="font-medium">{result.nom}</span>
                    {(() => {
                      const isVentile = result.type === 'ensemble' && result.ensembleMode === 'ventilation';
                      const badgeClass =
                        result.type === 'lot'
                          ? 'bg-blue-100 text-blue-800'
                          : result.type === 'prestation'
                          ? 'bg-green-100 text-green-800'
                          : isVentile
                          ? 'bg-violet-100 text-violet-800'
                          : 'bg-purple-100 text-purple-800';
                      const badgeLabel =
                        result.type === 'lot'
                          ? 'Lot'
                          : result.type === 'prestation'
                          ? 'Prestation'
                          : isVentile
                          ? 'Ventilé'
                          : 'Groupe';
                      return (
                        <span className={`px-2 py-1 text-xs rounded-full ${badgeClass}`}>
                          {badgeLabel}
                        </span>
                      );
                    })()}
                  </div>
                  <div className={`text-sm ${
                    index === selectedIndex ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {result.description}{result.unite ? ` • ${result.unite}` : ''}
                  </div>
                </div>
                {result.type === 'prestation' && (() => {
                  const ouvrage = result.data as Ouvrage;
                  const avg = computeBenchmarkStats(ouvrage).avg;
                  if (avg <= 0) return null;
                  return (
                    <span
                      className={`flex-shrink-0 px-2 py-0.5 text-xs rounded tabular-nums ${
                        index === selectedIndex
                          ? 'bg-violet-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                      title={`Moyenne des prix historiques sur ${ouvrage.chantiers.length} chantier${ouvrage.chantiers.length > 1 ? 's' : ''}`}
                    >
                      {BlockCalculationService.formatPrice(avg)} €/{ouvrage.unite}
                    </span>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message si aucun résultat */}
      {isOpen && searchQuery.length >= 2 && searchResults.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-2 px-3 text-sm text-gray-500">
          Aucun résultat trouvé pour "{searchQuery}"
        </div>
      )}
    </div>
  );
};

export default ChiffMenuSearchBar;
